import Vue, { VNode } from "vue";
import { DirectiveBinding } from "vue/types/options";
import { isSafari } from "mobile-device-detect";

export interface DraggableEvent {
  item: HTMLElement;
  originalEvent: DragEvent;
  draggedItem: HTMLElement;
}

export type DragHandler = (draggableEvent: DraggableEvent) => void;

const EVENT_TYPES = ( // "ondragstart,ondrag,ondragend,ondragenter,ondragover,ondragleave,ondrop," +
  "dragstart,dragexit,dragleave,dragend," +
  "dragover," +
  "drop," +
  "mousedown,mousenter,mouseleave,mousemove,mouseout,mouseover,mouseup," +
  "pointercancel,pointerdown,pointerenter,pointerleave,pointermove,pointerout,pointerover,pointerup," +
  "touchstart,touchmove,touchcancel,touchend"
).split(",");

function getDraggableContainer(node: HTMLElement): HTMLElement | null {
  for (
    let i = 5;
    node && !node.classList.contains("drag-container") && i >= 0;
    i--
  ) {
    node = node.parentElement as HTMLElement;
  }
  return node && node.classList.contains("drag-container") ? node : null;
}

export const DraggableContainer = Vue.directive("draggable-container", {
  bind(
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
    oldVnode: VNode
  ) {
    let debugMode = false;
    let dragStartHandler = null as DragHandler | null;
    let dragOverHandler = null as DragHandler | null;
    let dragEndHandler = null as DragHandler | null;
    let draggingClass = "dragging";
    let highlightClass = "highlight";
    if (binding.expression) {
      const component = vnode.context as any;
      const options = vnode.context?.$data[binding.expression];
      if (options) {
        if (options.debugMode) {
          debugMode = options.debugMode as boolean;
        }
        if (options.onDragStart) {
          const handler = component[options.onDragStart] as DragHandler;
          if (handler) {
            dragStartHandler = handler.bind(component);
          }
        }
        if (options.onDragOver) {
          const handler = component[options.onDragOver] as DragHandler;
          if (debugMode) {
            console.log("on DragOver handler");
            console.log(handler);
          }
          if (handler) {
            dragOverHandler = handler.bind(component);
          }
        }
        if (options.onDragEnd) {
          const handler = component[options.onDragEnd] as DragHandler;
          if (debugMode) {
            console.log("on DragEnd handler");
            console.log(handler);
          }
          if (handler) {
            dragEndHandler = handler.bind(component);
          }
        }
        if (options.draggingClass) {
          draggingClass = options.draggingClass as string;
        }
        if (options.highlightClass) {
          highlightClass = options.highlightClass as string;
        }
      }
      if (debugMode) {
        console.log(binding);
        console.log(options);
      }
    }
    el.classList.add("drag-container");

    const container = el as any;

    (el as any).genericEventHandler = (el: HTMLElement, ev: any) => {
      const isEvent = (types: string) => types.split(",").includes(ev.type);
      const dragging = !!container.draggedItem;
      const draggableEvent = {
        item: el,
        originalEvent: ev,
        draggedItem: container.draggedItem,
      } as DraggableEvent;

      if (!el.draggable) {
        // ev.stopPropagation();
        // ev.preventDefault();
        return;
      }

      if (!dragging && isEvent("touchstart,dragstart")) {
        if (debugMode) {
          console.log("Drag-Operation STARTET");
          console.log("Adding dragging css class " + draggingClass);
        }
        el.classList.add(draggingClass);
        container.draggedItem = el;
        ev.stopPropagation();
        if (ev.dataTransfer) {
          ev.dataTransfer.setData("text/plain", "some_dummy_data");
          ev.dataTransfer.effectAllowed = "move";
        }
        if (dragStartHandler) {
          dragStartHandler(draggableEvent);
        }
        return;
      }

      if (dragging) {
        if (
          isEvent(
            "dragenter,mouseenter,pointerenter,dragover,mouseover,pointerover"
          )
        ) {
          if (debugMode) {
            console.log("Adding higlighting css class " + highlightClass);
          }
          el.classList.add(highlightClass);
          if (dragOverHandler) {
            dragOverHandler(draggableEvent);
          }
          return;
        }

        if (isEvent("touchmove")) {
          ev.preventDefault();
          return;
        }

        if (isEvent("dragleave,mouseleave,pointerleave")) {
          if (debugMode) {
            console.log("Removing higlighting css class " + highlightClass);
          }
          el.classList.remove(highlightClass);
          return;
        }
      }

      if (dragging && isEvent("touchend,dragend")) {
        if (debugMode) {
          console.log("Drag-Operation ABGESCHLOSSEN");
        }
        if (dragEndHandler) {
          dragEndHandler(draggableEvent);
        }
        if (debugMode) {
          console.log("Removing dragging css class " + draggingClass);
        }
        el.classList.remove(draggingClass);
        delete container.draggedItem;
      }
    };

    const eventHandler = (ev: any) => (el as any).genericEventHandler(el, ev);
    "dragover".split(",").forEach((eventType) => {
      el.addEventListener(eventType, eventHandler, false);
    });
  },
  unbind(
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
    oldVnode: VNode
  ) {
    if (vnode.context) {
      const container = vnode.context as any;
      if (container.draggable) {
        delete container.draggable;
      }
    }
  },
});

export const DraggableRow = Vue.directive("draggable-row", {
  inserted(
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
    oldVnode: VNode
  ) {
    const container = getDraggableContainer(el);
    if (container) {
      const genericEventHandler = (container as any).genericEventHandler;
      const eventHandler = (ev: any) => genericEventHandler(el, ev);
      (el as any).eventHandler = eventHandler;

      EVENT_TYPES.forEach((eventType) => {
        el.addEventListener(eventType, eventHandler, false);
      });
    }
  },
  unbind(
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
    oldVnode: VNode
  ) {
    const container = getDraggableContainer(el);
    if (container) {
      const eventHandler = (container as any).eventHandler;
      EVENT_TYPES.forEach((eventType) => {
        el.removeEventListener(eventType, eventHandler, false);
      });
    }
  },
});
